import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, ReplaySubject } from 'rxjs';

import { Coords, DirectionsEvent, DirectionsEventType, DirectionsResponse, RoutingProfile } from '../models';

const accessToken: string = 'pk.eyJ1Ijoic25hemFtaSIsImEiOiJjaW1pMTBpcDEwMDNydmlrdW8yc2RoY29pIn0.R_8cMvgM26cjwwLW7rlBVQ';
const basePath: string = 'https://api.mapbox.com';
const directionsEndPoint: string = '/directions/v5/';

@Injectable({ providedIn: 'root' })
export class DirectionsService {

    private directionsEventSubject: BehaviorSubject<DirectionsEvent>;
    private directionsRspSubject: ReplaySubject<DirectionsResponse> = new ReplaySubject<DirectionsResponse>(1);

    constructor(private http: HttpClient) {
        // Initialize directionsEventSubject with stop type and none origin
        this.directionsEventSubject = new BehaviorSubject({
            type: DirectionsEventType.INITIAL
        });
    }

    /**
    * Function that can emit directionsEvents across app
    */
    public emitDirectionsEvent(ev: DirectionsEvent): BehaviorSubject<DirectionsEvent> {
        this.directionsEventSubject.next(ev);
        return this.watchDirectionsEvents();
    }

    /**
    * Reset directions (close directions bar and reset route on map)
    */
    public resetDirections(): void {
        let ev: DirectionsEvent = {
            type: DirectionsEventType.STOP_NAVIGATION
        };
        this.directionsEventSubject.next(ev);
    }

    /**
    * Function that allows listeners to watch directionsEvents across app
    */
    public watchDirectionsEvents(): BehaviorSubject<DirectionsEvent> {
        return this.directionsEventSubject;
    }

/**
    * Function that allows listeners to watch the current directions
    */
    public watchDirectionsResponse(): Observable<DirectionsResponse> {
        return this.directionsRspSubject.asObservable();
    }

    /**
    * Function that uses MapBox Directions API to fetch directions based on max 25 points (Coords[])
    */
    public async getDirections(points: Coords[], routingProfile: RoutingProfile = RoutingProfile.WALKING): Promise<DirectionsResponse> {
        const directions = await this.requestMapboxRoute(points, routingProfile).catch( err => {
            console.warn("Could not get mapbox directions", err);
            return null;
        });
        this.directionsRspSubject.next(directions);
        return directions;
    }

    private requestMapboxRoute(points: Coords[], routingProfile: RoutingProfile): Promise<DirectionsResponse> {
        return new Promise((resolve, reject) => {

            // Validate input
            if(points.length <= 0) return reject('Minimum of 2 points allowed');
            if(points.length >= 25) return reject('Maximum of 25 points allowed');
            else if(!points[0].isValid) return reject('First point is invalid');

            // Create encoded coordinates for request (is a loooong string that consists of lat,long;lat,long;lat...)
            let encodedCoords = '';
            for(let c of points) {
                encodedCoords += (encodeURIComponent(c.asString(',', true)) + ';');
            }
            encodedCoords = encodedCoords.substr(0, encodedCoords.length - 1); // Remove last ';' from string

            // Create request
            const request: string = basePath + directionsEndPoint + routingProfile + '/' + encodedCoords + '.json?' + 'access_token=' + accessToken + '&geometries=geojson&overview=full';

            this.http.get(request).toPromise().then((res: any) => {
                // Check res obj
                if(!res.routes || !res.routes[0] || !res.routes[0].geometry) {
                    return reject({ msg: 'missing information on res obj', obj: res });
                }

                // Find route in response and resolve
                let route = res.routes[0];
                const response: DirectionsResponse = {
                    geometry: route.geometry,
                    duration: route.duration,
                    distance: Math.round(route.distance),
                    legs: route.legs,
                    routingProfile: routingProfile
                }
                resolve(response);
            }).catch((err) => {
                reject(err);
            });
        });
    }
}
